import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Zap, X, Trophy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ArrowDisplay from "@/components/ArrowDisplay";
import TouchDPad from "@/components/practice/TouchDPad";
import useRapidFire from "@/components/practice/useRapidFire";
import { formatMs } from "@/utils/format";
import { play as sfx, playHover } from "@/utils/sounds";

const directionIcon = { up: ArrowUp, down: ArrowDown, left: ArrowLeft, right: ArrowRight };

const RapidFire = ({ stratagems, onExit }) => {
  const rf = useRapidFire(stratagems);

  // ── Countdown ──
  if (rf.phase === "countdown") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="rapid-fire-countdown">
        <motion.div
          key={rf.count}
          initial={{ scale: 2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="font-headings text-8xl text-[#FFE800] glow-yellow"
        >
          {rf.count || "GO"}
        </motion.div>
        <p className="mt-6 text-sm text-gray-500 font-data uppercase tracking-widest">Rapid Fire starting...</p>
      </div>
    );
  }

  // ── Results ──
  if (rf.phase === "results") {
    const metrics = [
      { label: "Completed", value: rf.completed, testId: "rf-result-completed" },
      { label: "Accuracy", value: `${rf.accuracy}%`, testId: "rf-result-accuracy" },
      { label: "Best Streak", value: rf.longestStreak, testId: "rf-result-streak" },
      { label: "SPM", value: rf.spm, testId: "rf-result-spm" },
    ];

    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto space-y-6" data-testid="rapid-fire-results">
        <div className="hud-card clip-corners p-8 text-center">
          <Trophy className="h-12 w-12 text-[#FFE800] mx-auto mb-4" />
          <h2 className="font-headings text-4xl uppercase tracking-widest text-[#FFE800]">Mission Complete</h2>
          <p className="mt-2 text-sm text-gray-400">Rapid Fire session ended</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {metrics.map(m => (
            <div key={m.testId} className="hud-card clip-corners p-4 text-center" data-testid={m.testId}>
              <p className="text-xs uppercase tracking-widest text-gray-500 font-headings">{m.label}</p>
              <p className="mt-1 font-data text-2xl text-[#FFE800]">{m.value}</p>
            </div>
          ))}
        </div>
        <div className="hud-card clip-corners p-4 text-center">
          <p className="text-xs text-gray-500 font-data">Session time: {formatMs(rf.elapsed)}</p>
          <p className="text-xs text-gray-600 font-data mt-1">{rf.attempts} attempts across random stratagems</p>
        </div>
        <div className="flex justify-center gap-4">
          <button onClick={() => { sfx("click"); rf.restart(); }} onMouseEnter={playHover}
            className="border border-[#FFE800] text-[#FFE800] px-6 py-3 font-headings text-lg uppercase tracking-widest hover:bg-[#FFE800]/10 transition-colors duration-150"
            data-testid="rf-play-again">Play Again</button>
          <button onClick={() => { sfx("back"); onExit(); }} onMouseEnter={playHover}
            className="border border-white/20 text-gray-400 px-6 py-3 font-headings text-lg uppercase tracking-widest hover:text-white hover:border-white/40 transition-colors duration-150"
            data-testid="rf-exit">Exit</button>
        </div>
      </motion.div>
    );
  }

  // ── Active ──
  const flashBorder = rf.flash === "success" ? "border-green-500/60" : rf.flash === "fail" ? "border-red-500/60" : "border-white/10";
  const flashBg = rf.flash === "success" ? "bg-green-500/5" : rf.flash === "fail" ? "bg-red-500/5" : "";

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="rapid-fire-active">
      {/* Live stats bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <RfStat label="Completed" value={rf.completed} testId="rf-stat-completed" />
          <RfStat label="Streak" value={rf.streak} testId="rf-stat-streak" />
          <RfStat label="Accuracy" value={`${rf.accuracy}%`} color="text-white" testId="rf-stat-accuracy" />
          <RfStat label="SPM" value={rf.spm} color="text-white" testId="rf-stat-spm" className="hidden md:block" />
        </div>
        <div className="flex items-center gap-3">
          <span className="font-data text-sm text-gray-500">{formatMs(rf.elapsed)}</span>
          <button onClick={rf.endSession} onMouseEnter={playHover}
            className="flex items-center gap-1 border border-red-500/40 text-red-400 px-3 py-1 text-xs font-headings uppercase tracking-widest hover:bg-red-500/10 transition-colors duration-150"
            data-testid="rf-end-session">
            <X className="h-3 w-3" /> End
          </button>
        </div>
      </div>

      {/* Main stratagem card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={rf.current?.id}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.2 }}
          className={`hud-card clip-corners p-8 border-2 transition-colors duration-200 ${flashBorder} ${flashBg}`}
          data-testid="rf-stratagem-card"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500" data-testid="rf-category">{rf.current?.category}</p>
          <h2 className="mt-2 font-headings text-3xl md:text-4xl uppercase tracking-widest text-white" data-testid="rf-stratagem-name">{rf.current?.name}</h2>
          <div className="mt-6">
            <ArrowDisplay sequence={rf.current?.code || []} progress={rf.inputSeq.length} testIdPrefix="rf-target" />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="rf-input-buffer">
            {rf.inputSeq.length === 0 ? (
              <span className="text-xs text-gray-500">Awaiting input...</span>
            ) : (
              rf.inputSeq.map((dir, i) => {
                const Icon = directionIcon[dir];
                return (
                  <span key={`${dir}-${i}`} className="flex items-center justify-center rounded-sm border border-white/10 bg-black/50 p-2 text-[#FFE800]">
                    <Icon className="h-5 w-5" />
                  </span>
                );
              })
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {rf.nextUp && (
        <div className="flex items-center gap-3 text-xs text-gray-600 font-data" data-testid="rf-next-up">
          <span className="uppercase tracking-widest">Next up:</span>
          <span className="text-gray-400">{rf.nextUp.name}</span>
        </div>
      )}

      {rf.isTouchDevice && (
        <div className="min-h-[140px]">
          <TouchDPad onInput={rf.handleInput} isLocked={rf.lockUntil && Date.now() < rf.lockUntil} />
        </div>
      )}
    </div>
  );
};

const RfStat = ({ label, value, color = "text-[#FFE800]", testId, className = "" }) => (
  <div className={className} data-testid={testId}>
    <span className="text-xs uppercase tracking-widest text-gray-500 font-headings">{label}</span>
    <span className={`ml-2 font-data text-xl ${color}`}>{value}</span>
  </div>
);

export default RapidFire;
